// hebrewPhoneticUtils.js

/**
 * Hebrew Phonetic Normalizer
 * Normalizes Hebrew text for phonetic matching to catch sound-alike words
 */

// Check if text contains Hebrew characters
export const isHebrewText = (text) => {
  return /[\u0590-\u05FF]/.test(text);
};

// Check if text contains mixed Hebrew and English
export const isMixedHebrewEnglish = (text) => {
  const hasHebrew = /[\u0590-\u05FF]/.test(text);
  const hasEnglish = /[a-zA-Z]/.test(text);
  return hasHebrew && hasEnglish;
};

// Split mixed Hebrew-English text into words, preserving each word's language
export const splitMixedText = (text) => {
  if (!text) return [];
  // Split by spaces and filter out empty strings
  return text.split(/\s+/).filter(word => word.length > 0);
};

// Hebrew phonetic normalization rules
export const hebrewPhoneticNormalize = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  let normalized = text.toLowerCase();
  
  // Remove nikud (vowel marks) if present
  normalized = normalized.replace(/[\u0591-\u05C7]/g, '');
  
  // Remove silent ה at the end of words
  normalized = normalized.replace(/ה$/g, '');
  
  // Handle vowel letters (matres lectionis) - normalize their positions
  const words = normalized.split(/\s+/);
  const normalizedWords = words.map(word => {
    if (!isHebrewText(word)) return word;
    
    const chars = word.split('');
    const vowelLetters = [];
    const consonants = [];
    
    chars.forEach(char => {
      if ('איו'.includes(char)) {
        vowelLetters.push(char);
      } else {
        consonants.push(char);
      }
    });
    
    // Reconstruct word with vowel letters at the end (standardized position)
    return consonants.join('') + vowelLetters.sort().join('');
  });
  
  normalized = normalizedWords.join(' ');
  
  // Handle common sound-alike letter substitutions
  const soundAlikeMap = {
    'כ': 'ק',  // kaf/qof
    'ך': 'ק',  // final kaf/qof
    'ב': 'v',  // bet/vet distinction
    'ו': 'v',  // vav as 'v' sound
    'ת': 'ט',  // tav/tet
    'ס': 'צ',  // samech/tsadi (in some accents)
    'ץ': 'צ',  // final tsadi
  };
  
  // Apply sound-alike substitutions
  Object.entries(soundAlikeMap).forEach(([from, to]) => {
    normalized = normalized.replace(new RegExp(from, 'g'), to);
  });
  
  return normalized;
};

// Create a phonetic signature that's even more forgiving
export const hebrewPhoneticSignature = (text) => {
  if (!text || !isHebrewText(text)) return text;
  
  const normalized = hebrewPhoneticNormalize(text);
  
  // Further simplification: remove all vowel letters entirely for signature
  let signature = normalized.replace(/[איו]/g, '');
  
  // Sort remaining characters to catch more variations
  // This is aggressive but catches more edge cases
  const chars = signature.split('');
  signature = chars.sort().join('');
  
  return signature;
};

// Compare two Hebrew words phonetically
export const areHebrewWordsPhoneticallySimilar = (word1, word2) => {
  if (!word1 || !word2) return false;
  
  // Common Hebrew prefixes
  const prefixes = ['ב', 'ה', 'ו', 'כ', 'ל', 'מ', 'ש'];
  
  // Normalize both words
  const norm1 = hebrewPhoneticNormalize(word1);
  const norm2 = hebrewPhoneticNormalize(word2);
  
  // Direct match
  if (norm1 === norm2) return true;
  
  // Try removing prefixes and comparing
  for (const prefix of prefixes) {
    if (word1.startsWith(prefix)) {
      const word1WithoutPrefix = word1.slice(1);
      const norm1WithoutPrefix = hebrewPhoneticNormalize(word1WithoutPrefix);
      if (norm1WithoutPrefix === norm2) return true;
    }
    if (word2.startsWith(prefix)) {
      const word2WithoutPrefix = word2.slice(1);
      const norm2WithoutPrefix = hebrewPhoneticNormalize(word2WithoutPrefix);
      if (norm1 === norm2WithoutPrefix) return true;
    }
  }
  
  return false;
};

// Debug helper - useful for testing
export const debugPhoneticNormalization = (word) => {
  console.log('Original:', word);
  console.log('Normalized:', hebrewPhoneticNormalize(word));
  console.log('Signature:', hebrewPhoneticSignature(word));
};